import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { BeforeUnloadService } from '@app/core/services/before-unload.service';
import { HeaderComponent } from '@shared/components/header/header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { FormGroupType } from '@app/core/utils/formgroup-type';
import {
  CreateMeteringPointCommand,
  MeteringPointsService,
  MeteringPointSubType,
  MeteringPointType,
} from '@app/api/dmc';
import { LucideAngularModule } from 'lucide-angular';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { ReactiveFormsModule } from '@angular/forms';
import { createMeteringPointFormGroup } from '@shared/forms/create-meteringpoint.formgroup';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { ToastService } from '@app/core/services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '@app/core/services/loading.service';
import { FormConfig } from '@shared/interfaces/form-configs/form-config';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { applyFormConfig } from '@app/core/utils/apply-form-config';
import { CardComponent } from '@shared/components/card/card.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import { AddressDto } from '@app/api/nts';
import { logFormErrors } from '@app/core/utils/log-form-errors';
import {
  MeteringpointFormCreationDetailsComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-creation-details/meteringpoint-form-creation-details.component';
import {
  MeteringpointFormInstallationComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-installation/meteringpoint-form-installation.component';
import {
  MeteringpointFormReadingBillingComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-reading-billing/meteringpoint-form-reading-billing.component';
import {
  MeteringpointFormProductionComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-production/meteringpoint-form-production.component';
import {
  MeteringpointFormMeterComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-meter/meteringpoint-form-meter.component';
import {
  MeteringpointFormAddressComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-address/meteringpoint-form-address.component';
import {
  MeteringpointFormExchangeComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-exchange/meteringpoint-form-exchange.component';
import { NgClass } from '@angular/common';
import { meteringPointTypeConfig } from '@views/meteringpoint/configs/meteringpointtype-config';
import {
  meteringPointTypeTemplateConsumptionFlexFormConfig
} from '@views/meteringpoint/configs/meteringpointtype-template-consumption-flex.form-config';
import {
  meteringPointTypeTemplateConsumptionHourFormConfig
} from '@views/meteringpoint/configs/meteringpointtype-template-consumption-hour.form-config';
import {
  meteringPointTypeTemplateProductionQuarterlyFormConfig
} from '@views/meteringpoint/configs/meteringpointtype-template-production-quarterly.form-config';
import {
  meteringPointTypeTemplateSupplyToGridQuarterlyFormConfig
} from '@views/meteringpoint/configs/meteringpointtype-template-supply-to-grid-quarterly.form-config';
import {
  meteringPointTypeTemplateConsumptionFromGridQuarterlyFormConfig
} from '@views/meteringpoint/configs/meteringpointtype-template-consumption-from-grid-quarterly.from-config';
import {
  MeteringPointFormHelperService
} from '@views/meteringpoint/services/meteringpoint-form-helper.service';

@Component({
  selector: 'app-meteringpoint-create',
  imports: [
    HeaderComponent,
    TranslatePipe,
    LucideAngularModule,
    InputGroupComponent,
    LabelDirective,
    SelectDirective,
    ReactiveFormsModule,
    InputDirective,
    GuideCardComponent,
    CardComponent,
    ButtonDirective,
    MeteringpointFormCreationDetailsComponent,
    MeteringpointFormInstallationComponent,
    MeteringpointFormReadingBillingComponent,
    MeteringpointFormProductionComponent,
    MeteringpointFormMeterComponent,
    MeteringpointFormAddressComponent,
    MeteringpointFormExchangeComponent,
    NgClass,
  ],
  templateUrl: './meteringpoint-create.component.html',
  styleUrl: './meteringpoint-create.component.css'
})
export class MeteringpointCreateComponent implements OnInit, AfterViewInit, HasUnsavedChanges {
  public form: FormGroupType<CreateMeteringPointCommand> = createMeteringPointFormGroup();

  public meteringPointTypeList: KeyValue[] = enumToKeyValue(MeteringPointType, { omitKeys: ['NotSpecified', 'SurplusProductionGroup'], prefixValue: 'enum.meteringpoint.meteringpointtype.' });
  public meteringPointSubtypeList: KeyValue[] = enumToKeyValue(MeteringPointSubType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.meteringpointsubtype.' });

  public completeGridAreaList: KeyValue[] = [];

  protected readonly MeteringPointType = MeteringPointType;
  protected readonly MeteringPointSubType = MeteringPointSubType;

  public childMeteringPoint: boolean = false;

  public configs: Partial<Record<MeteringPointType, FormConfig<CreateMeteringPointCommand>>> = meteringPointTypeConfig;

  public templateList: KeyValue[] = [
    {
      key: 'Template.ConsumptionVirtualFlex',
      value: 'meteringpoint.template.templateconsumptionvirtualflex'
    },
    {
      key: 'Template.ConsumptionVirtualHour',
      value: 'meteringpoint.template.templateconsumptionvirtualhour'
    },
    {
      key: 'Template.ProductionVirtualQuarterly',
      value: 'meteringpoint.template.templateproductionvirtualquarterly'
    },
    {
      key: 'Template.SupplyToGridQuarterly',
      value: 'meteringpoint.template.templatesupplytogridquarterly'
    },
    {
      key: 'Template.ConsumptionFromGrid',
      value: 'meteringpoint.template.templateconsumptionfromgridquarterly'
    }
  ];

  private readonly templates: Partial<Record<string, FormConfig<CreateMeteringPointCommand>>> = {
    'Template.ConsumptionVirtualFlex': meteringPointTypeTemplateConsumptionFlexFormConfig,
    'Template.ConsumptionVirtualHour': meteringPointTypeTemplateConsumptionHourFormConfig,
    'Template.ProductionVirtualQuarterly': meteringPointTypeTemplateProductionQuarterlyFormConfig,
    'Template.SupplyToGridQuarterly': meteringPointTypeTemplateSupplyToGridQuarterlyFormConfig,
    'Template.ConsumptionFromGrid': meteringPointTypeTemplateConsumptionFromGridQuarterlyFormConfig
  };

  private ignoreConfig = false;

  constructor(
    private readonly meteringPointsService: MeteringPointsService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly loadingService: LoadingService,
    private readonly route: ActivatedRoute,
    private readonly formHelper: MeteringPointFormHelperService,
  ) {
    this.formHelper.loadGridAreas().subscribe(areas => this.completeGridAreaList = areas);
    inject(BeforeUnloadService).register(() => this.hasUnsavedChanges(), inject(DestroyRef));
  }

  public ngOnInit(): void {
    this.form.get('detailCharacteristics.meteringPointType')?.valueChanges.subscribe((type: MeteringPointType) => {
      if (this.ignoreConfig) {
        this.ignoreConfig = false;
        return;
      }

      if (type && this.configs[type]) {
        applyFormConfig<CreateMeteringPointCommand>(this.form, this.configs[type]);
      }
    });

    this.formHelper.setupSubTypeHandling(this.form);

    this.form.get('templateId')?.valueChanges.subscribe((templateId: string) => {
      this.applyTemplate(templateId);
    });

    this.formHelper.setupParentMeteringPointHandling(this.form, isChild => this.childMeteringPoint = isChild);
    this.formHelper.registerConnectionPointChange(this.form);
  }

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public ngAfterViewInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['meteringPointType']) {
        this.form.get('detailCharacteristics.meteringPointType')?.setValue(params['meteringPointType']);
      }

      if (params['connectionPointIdentification']) {
        this.formHelper.populateFromConnectionPointID(this.form, params['connectionPointIdentification']);
      }

      this.form.markAsPristine();
    });
  }

  private applyTemplate(templateId: string): void {
    if (!templateId || !this.templates[templateId]) return;

    this.ignoreConfig = true;
    applyFormConfig<CreateMeteringPointCommand>(this.form, this.templates[templateId], true);
  }

  public addressChanged(address: AddressDto): void {
    if (address) {
      this.form.get('installationLocationAddress')?.patchValue(address);
    } else {
      this.form.get('installationLocationAddress')?.setValue({});
    }
  }

  public create(): void {
    if (this.form.invalid) {
      logFormErrors(this.form);
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      return;
    }

    this.form.get('installationLocationAddress')?.enable();
    this.form.get('templateId')?.disable({ emitEvent: false });

    const form = this.form.value as CreateMeteringPointCommand;
    const data = removeNullKeys(form);

    this.loadingService.start();

    this.meteringPointsService.meteringPointsPost(data).subscribe({
      next: (data) => {
        this.toastService.show('meteringPoint.create.success');
      },
      error: (error) => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      },
      complete: () => {
        this.loadingService.stop();
        this.form.markAsPristine();
        this.router.navigate(
          ['meteringpoint'],
          { queryParams: {
              streetName: form.installationLocationAddress?.streetName,
              buildingNumber: form.installationLocationAddress?.buildingNumber,
              postalCode: form.installationLocationAddress?.postalCode,
              floorIdentification: form.installationLocationAddress?.floorIdentification,
            }
          });
      },
    });
  }

  public cancel(): void {
    this.router.navigate(['/meteringpoint']);
  }

  public openMeteringPointModal(): void {
    this.formHelper.openMeteringPointModal().subscribe((meteringPointId: string) => {
      this.form.get('parentMeteringPointIdentification')?.patchValue(meteringPointId);
    });
  }

  public openConnectionPointSearchModal(): void {
    this.formHelper.openConnectionPointSearchModal().subscribe((data) => {
      this.formHelper.populateAddressFormFromConnectionPoint(this.form, data);
    });
  }
}
